import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';
import {
  addLesson,
  deleteLesson,
  getFormation,
  type FormationTree,
  type TrainingLesson,
} from '../services/formationsAdminApi';

const LevelEditorPage: React.FC = () => {
  const { formationId, levelId } = useParams<{ formationId: string; levelId: string }>();
  const { t } = useTranslation();
  const { navigateWithLang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tree, setTree] = useState<FormationTree | null>(null);
  const [lessons, setLessons] = useState<TrainingLesson[]>([]);
  const [newLessonCode, setNewLessonCode] = useState('');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!formationId || !levelId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getFormation(formationId);
      const data = res.data;
      if (!data) throw new Error('Formation not found');
      setTree(data);
      const level = data.levels.find((l) => l._id === levelId);
      if (!level) throw new Error('Level not found');
      setLessons(level.lessons || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load level');
    } finally {
      setLoading(false);
    }
  }, [formationId, levelId]);

  useEffect(() => {
    load();
  }, [load]);

  const level = tree?.levels.find((l) => l._id === levelId);

  const handleAddLesson = async () => {
    if (!levelId || !newLessonCode.trim()) return;
    setSaving(true);
    try {
      await addLesson(levelId, {
        code: newLessonCode.trim(),
        locales: {
          en: { title: newLessonTitle.trim() || newLessonCode.trim(), summary: '', objectives: [] },
          fr: { title: newLessonTitle.trim() || newLessonCode.trim(), summary: '', objectives: [] },
        },
      });
      setNewLessonCode('');
      setNewLessonTitle('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lesson: TrainingLesson) => {
    if (!window.confirm(t('formations.lesson.deleteConfirm', { defaultValue: 'Delete this lesson?' }))) return;
    setSaving(true);
    try {
      await deleteLesson(lesson._id);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ContentPageLayout>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigateWithLang(`/formations/${formationId}`)}
        sx={{ mb: 2 }}
      >
        {t('formations.backFormation', { defaultValue: 'Back to formation' })}
      </Button>

      <Typography variant="h5" gutterBottom sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>
        {level?.code} — {level?.locales?.fr?.title || level?.locales?.en?.title}
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('formations.lesson.add', { defaultValue: 'Add lesson' })}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <TextField size="small" label="Code" value={newLessonCode} onChange={(e) => setNewLessonCode(e.target.value)} />
          <TextField size="small" label="Title" value={newLessonTitle} onChange={(e) => setNewLessonTitle(e.target.value)} />
          <Button variant="contained" startIcon={<AddIcon />} disabled={saving || !newLessonCode.trim()} onClick={handleAddLesson}>
            {t('formations.lesson.addButton', { defaultValue: 'Add' })}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>#</TableCell>
              <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.code', { defaultValue: 'Code' })}</TableCell>
              <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.title', { defaultValue: 'Title' })}</TableCell>
              <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.documents', { defaultValue: 'Docs' })}</TableCell>
              <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lessons.map((lesson) => (
              <TableRow key={lesson._id} hover>
                <TableCell>{lesson.order}</TableCell>
                <TableCell>{lesson.code}</TableCell>
                <TableCell>{lesson.locales?.fr?.title || lesson.locales?.en?.title}</TableCell>
                <TableCell align="right">{lesson.documents?.length ?? 0}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      navigateWithLang(`/formations/${formationId}/levels/${levelId}/lessons/${lesson._id}`)
                    }
                    aria-label={t('formations.lesson.edit', { defaultValue: 'Edit' })}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteLesson(lesson)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </ContentPageLayout>
  );
};

export default LevelEditorPage;
